/**
 * Alle zichtbare tekst van de applicatie.
 *
 * Eén bestand, gewone strings, geen logica — zodat iemand die geen TypeScript
 * kent een slecht geformuleerde zin kan verbeteren zonder iets kapot te maken
 * (NFR-4). Nooit Nederlandse tekst rechtstreeks in een component.
 *
 * Vlaams Nederlands. In contracten en facturen spreken we de huurder aan met
 * "u", in het aanvraagformulier en op de site met "je".
 */

import type { PriceLineKind } from "@bivak/domain";

export const nl = {
  app: {
    naam: "Verhuur",
    organisatie: "Chiro Sint-Jozef Rijkevorsel",
    ondertitel: "Huur onze lokalen voor een weekend, een kamp of een dag",
    logoAlt: "Chirologo",
    demoBanner:
      "Demo — alle boekingen, prijzen en gegevens zijn fictief. Echt reserveren kan hier nog niet.",
  },

  nav: {
    kalender: "Kalender",
    praktisch: "Praktisch",
    aanvraag: "Aanvraag",
    beheer: "Beheer",
  },

  algemeen: {
    laden: "Bezig met laden…",
    // Ook de reservetekst wanneer de server geen leesbaar antwoord gaf.
    fout: "Er ging iets mis. Probeer het later opnieuw.",
    opnieuwProberen: "Opnieuw proberen",
    annuleren: "Annuleren",
    bewaren: "Bewaren",
    verder: "Verder",
    terug: "Terug",
  },

  kalender: {
    titel: "Beschikbaarheid",
    intro: "Huur per dag, per weekend of voor een kamp. Een aanvraag is vrijblijvend.",
    // Functions: two month cards each carry their own ‹ ›, so the label names
    // the month or a screen reader hears four indistinguishable buttons.
    vorigeMaanden: (maand: string) => `Vorige maanden (voor ${maand})`,
    volgendeMaanden: (maand: string) => `Volgende maanden (na ${maand})`,
    vrij: "vrij",
    bezet: "bezet",
    wisseldag: "wisseldag: vertrek 's middags, nieuwe aankomst kan",
    eigenWerking: "eigen werking chiro",
    autoBijgewerkt: "Deze kalender wordt automatisch bijgewerkt.",
    naarKalender: "Bekijk de kalender ↓",
    types: {
      dag: {
        naam: "Dagverhuur",
        periode: "het hele jaar",
        troeven: ["Eén kalenderdag", "Zaal, keuken en sanitair", "Tot 80 personen"],
      },
      weekend: {
        naam: "Weekendverhuur",
        periode: "september – juni",
        troeven: ["Vrijdag tot zondag", "Alle lokalen en slaapzalen", "60 slaapplaatsen"],
      },
      kamp: {
        naam: "Kampverhuur",
        periode: "juli – augustus",
        troeven: [
          "2 extra lokalen en extra douches",
          "Grote weide en kampvuurkring",
          "60 slaapplaatsen",
        ],
      },
    },
    prijsPerDag: (bedrag: string) => `${bedrag} per dag`,
    prijsPerPersoonNacht: (bedrag: string) => `${bedrag} p.p. per nacht`,
    prijsMinimum: (bedrag: string) => `minimum ${bedrag}`,
    selectie: {
      hint: "Duid je aankomstdag aan en daarna je vertrekdag. Reserveren kan meteen daarna.",
      kiesVertrek: "Duid nu je vertrekdag aan.",
      reserveer: "Reserveer",
      wissen: "Wis selectie",
      fouten: {
        bezet: "Die periode is al (deels) bezet.",
        seizoensgrens: "Kies datums binnen het schooljaar of binnen de zomer.",
        geen_kamp_in_schooljaar:
          "Verblijven langer dan een weekend kunnen enkel in juli en augustus.",
        ongeldig: "Die datums kloppen niet.",
      },
    },
  },

  praktisch: {
    titel: "Praktisch",
    gebouw: {
      titel: "Het gebouw",
      plattegrondNoot: "Plattegrond en aantallen zijn placeholders.",
      plan: {
        label: "Plattegrond",
        placeholder: "PLACEHOLDER",
        gelijkvloers: "gelijkvloers",
        verdieping: "verdieping",
        zaal: "GROTE ZAAL",
        zaalOppervlakte: "± 190 m²",
        keuken: "KEUKEN",
        sanitair: "SANITAIR",
        trap: "TRAP",
        slaapzaalBoven: "SLAAPZAAL BOVEN",
        slaapzaalZolder: "SLAAPZAAL ZOLDER",
        bedden: (aantal: number) => `${aantal} bedden`,
      },
      // ?? = bewust placeholder: de echte aantallen moeten nog geteld worden.
      troeven: [
        "?? slaapplaatsen, tot ?? personen",
        "?? douches en ?? wc's · 2 groepsdouches bij kampverhuur",
        "Volledig uitgeruste keuken",
        "Grote weide en kampvuurkring",
        "Tafels en stoelen inbegrepen",
      ],
      troevenNoot: "Aantallen en uren zijn placeholders, nog na te tellen.",
      fotos: {
        titel: "Foto's",
        volgt: "foto volgt",
        tegels: ["De grote zaal", "De slaapzalen", "De weide en de vuurkring", "De keuken"],
      },
    },
    ligging: {
      titel: "Ligging",
      adres: "Zuiderdijk 10E, 2310 Rijkevorsel",
      noot: "Parkeren op eigen terrein; fietsen via het jaagpad langs het kanaal.",
      opKaart: "Toon op Google Maps",
      kaartTitel: "Ligging op Google Maps",
    },
    faq: {
      titel: "Veelgestelde vragen",
      vragen: [
        {
          vraag: "Houdt mijn aanvraag de datums al vast?",
          antwoord:
            "Nee. Pas bij een optie staan de datums twee weken vast, definitief vanaf de bevestiging.",
        },
        {
          vraag: "Hoe werkt de waarborg?",
          antwoord:
            "Je stort de waarborg voor je verblijf. Na afloop trekken we verbruik en eventuele schade af en storten we de rest terug.",
        },
        {
          vraag: "Hoe wordt energie afgerekend?",
          antwoord:
            "Bij aankomst en vertrek lezen we samen de meters af. Je betaalt wat je verbruikte, aan de eenheidsprijzen uit je contract.",
        },
        {
          vraag: "Mogen we een kampvuur maken?",
          antwoord:
            "In de vuurkring op de weide, na afspraak en volgens de gemeentelijke regels. Hout breng je zelf mee.",
        },
        {
          vraag: "Moeten we zelf poetsen?",
          antwoord: "Je laat het gebouw bezemschoon achter. De eindschoonmaak doen wij.",
        },
      ],
    },
  },

  aanvraag: {
    titel: "Vraag een verblijf aan",
    gekozenType: "Formule",
    velden: {
      aankomst: "Aankomst",
      vertrek: "Vertrek",
      kiesInKalender: "Kies in de kalender",
      personen: "Geschat aantal personen",
      groepsnaam: "Naam van de groep",
      doel: "Doel van het verblijf",
      referentie: "Jullie referentie",
      referentieHint: "Optioneel — bijvoorbeeld een nummer voor jullie eigen boekhouding.",
      opmerking: "Laat een bericht achter",
      opmerkingHint: "Optioneel — een vraag of iets wat we zeker moeten weten.",
      contactNaam: "Contactpersoon",
      email: "E-mail",
      emailHint: "Hierop krijg je ons antwoord. Geen account nodig.",
      telefoon: "Telefoon",
    },
    prijs: {
      titel: "Richtprijs",
      sub: "op basis van je schatting — rekent live mee",
      totaal: "Totaal",
      // The dimensions named, not "40 × € 4,00" — nobody knows what a 40 is
      // (persona review; Kampas' table names its columns for the same reason).
      nachtenMaalPersonen: (nachten: number, personen: number) =>
        `${nachten} ${nachten === 1 ? "nacht" : "nachten"} × ${personen} pers.`,
      lijn: {
        overnight_persons: "Verblijf",
        day: "Dagverhuur",
        minimum_top_up: "Bijpassing minimumtarief",
      } satisfies Record<PriceLineKind, string> as Record<string, string>,
    },
    kalenderHint: "Duid je datums aan — bezette dagen zie je meteen.",
    fouten: {
      omgekeerd: "Vertrek ligt voor aankomst.",
      teVeel: "Tot 80 personen.",
      bezet: "Die periode is al (deels) bezet — kies andere datums.",
    },
    // Named per field, so the disabled verstuurknop can say WHY (user
    // feedback: a dead button without a reason is a dead end).
    nogNodig: (velden: string) => `Vul nog aan: ${velden}.`,
    veldnamen: {
      datums: "de datums",
      personen: "het geschatte aantal personen",
      groepsnaam: "de groepsnaam",
      contactNaam: "de contactpersoon",
      email: "een geldig e-mailadres",
      telefoon: "een telefoonnummer",
    },
    verstuur: "Verstuur aanvraag",
    bezig: "Versturen…",
    vrijblijvend: "Vrijblijvend: je aanvraag houdt de datums nog niet vast.",
    antwoordBelofte: "We antwoorden binnen enkele dagen per e-mail.",
    succes: {
      titel: "Aanvraag verstuurd",
      tekst:
        "We antwoorden binnen enkele dagen per e-mail. Je aanvraag houdt nog geen datums vast.",
      nieuwe: "Nieuwe aanvraag",
    },
  },

  beheer: {
    titel: "Beheer",
    boekingen: "Boekingen",
    kalender: "Kalender",
    afrekeningen: "Afrekeningen",
    demoNoot: "Demo: één gedeelde beheerderscode, nog geen persoonlijke accounts.",
    demoReset: {
      knop: "Demo herstellen",
      // Honest about the blast radius: every change since the vorige reset
      // verdwijnt, niet alleen "de demo wordt ververst".
      bevestig:
        "Alles wat sinds de vorige reset is aangepast of toegevoegd wordt gewist. " +
        "De demogegevens komen terug. Doorgaan?",
      bezig: "Bezig met herstellen…",
      gelukt: "Demo hersteld.",
      fout: "Herstellen mislukt. Probeer het opnieuw.",
    },
    groepen: {
      vraagtActie: "Vraagt actie",
      komend: "Komende verblijven",
      afgerond: "Afgerond",
      zonderGevolg: "Zonder gevolg",
    },
    hints: {
      nieuweAanvraag: "nieuwe aanvraag",
      optieVervalt: (dagen: number) =>
        dagen < 0
          ? "optie verlopen: beslissen"
          : dagen === 0
            ? "optie vervalt vandaag"
            : `optie vervalt over ${dagen} ${dagen === 1 ? "dag" : "dagen"}`,
      afTeRekenen: "verblijf voorbij, af te rekenen",
      verblijfVoorbij: "verblijf voorbij, nog te registreren",
    },
    lijst: {
      personen: (aantal: number) => `${aantal} pers.`,
      nachten: (aantal: number) => `${aantal} ${aantal === 1 ? "nacht" : "nachten"}`,
      dag: "dag",
      leeg: "Nog geen boekingen.",
      eigenWerking: "eigen werking",
    },
    detail: {
      terug: "Boekingen",
      verblijf: "Verblijf",
      dagverhuur: "dagverhuur",
      overnachting: "met overnachting",
      wat: "Wat",
      aankomst: "Aankomst",
      vertrek: "Vertrek",
      doel: "Doel",
      personen: "Personen",
      aangevraagd: "aangevraagd",
      onder31: "onder 31",
      referentie: "Referentie",
      bericht: "Bericht",
      contact: "Contact",
      prijsTitel: "Prijs",
      prijsRichtprijs: "Richtprijs. Ligt vast bij bevestiging.",
      prijsBevroren:
        "Vastgelegd bij bevestiging. Aanpassen kan enkel met een reden, en elke aanpassing blijft zichtbaar.",
      totaal: "Totaal",
      geenPrijs: "Geen prijs: eigen gebruik van de chiro wordt niet gefactureerd.",
      verloop: "Verloop",
      waarborg: "Waarborg",
      waarborgToekomst:
        "Wordt gevraagd bij bevestiging en na het verblijf verrekend met verbruik en eventuele schade.",
      waarborgEigenWerking: "Eigen werking: geen waarborg.",
      waarborgGevraagd: "gevraagd",
      waarborgOntvangen: "ontvangen",
      waarborgIngehouden: "ingehouden",
      waarborgTerugbetaald: "terugbetaald",
      optieVervaltOp: (moment: string) => `Optie vervalt automatisch op ${moment}.`,
      wijzig: "Wijzig",
      opslaan: "Bewaar",
      // Never "Annuleer": with the edit form open that word sat one tap away
      // from the booking-cancelling button of the same name (persona review).
      annuleer: "Sluit zonder bewaren",
      doelLeeg: "geen doel opgegeven",
      nietGevonden: "Deze boeking bestaat niet (meer).",
    },
    tarieven: {
      titel: "Tarieven",
      uitleg:
        "Prijzen zijn versies: een nieuw tarief gaat in op een gekozen datum. Oude versies blijven bestaan, zodat oude afrekeningen blijven kloppen.",
      verhuurKop: "Verhuur",
      energieKop: "Energie",
      nieuwTarief: "Nieuw tarief",
      annuleer: "Annuleer",
      publiceer: "Publiceer",
      ontbreekt: "Tarief ontbreekt — publiceer er een.",
      velden: {
        perDag: "Prijs per dag (€)",
        perPersoonNacht: "Per persoon per nacht (€)",
        minimum: "Minimum (€)",
        waarborg: "Waarborg (€)",
        vanaf: "Geldig vanaf",
        prijs: "Nieuwe prijs (€)",
      },
      waarborgLijn: (bedrag: string) => `waarborg ${bedrag}`,
      gepland: (datum: string) => `gepland vanaf ${datum}`,
      ongeldigBedrag: "Geen geldig bedrag.",
      sinds: (datum: string) => `sinds ${datum}`,
      historiekKop: "vroegere prijzen",
      perEenheid: (bedrag: string, eenheid: string) => `${bedrag} per ${eenheid}`,
      meters: {
        electricity: "Elektriciteit",
        gas: "Gas",
        water: "Water",
      } as Record<string, string>,
    },
    kalenderScherm: {
      uitleg: "Wie het gebouw wanneer heeft. Tik een dag voor de boeking.",
      eerdereMaanden: (aantal: number) =>
        aantal === 1 ? "Eerdere maand tonen" : `Eerdere maanden tonen · ${aantal}`,
      gesloten: "Gesloten",
      vertrekt: "vertrekt",
      legende: {
        option: "In optie",
        confirmed: "Bevestigd",
        stayed: "Verblijf afgerond",
        settled: "Afgerekend of afgesloten",
        blokkade: "Gesloten (eigen werking of onderhoud)",
      },
    },
    login: {
      titel: "Beheer",
      uitleg: "Geef de beheerderscode om verder te gaan.",
      veld: "Beheerderscode",
      knop: "Ga verder",
      fout: "Die code klopt niet.",
      terug: "Naar de site",
    },
    afmelden: "Afmelden",
    afrekening: {
      titel: "Eindafrekening",
      afTeRekenen: "Af te rekenen",
      afgerekend: "Afgerekend",
      leeg: "Niets af te rekenen.",
      meters: { electricity: "Elektriciteit", gas: "Gas", water: "Water" },
      eenheid: (unit: string) => (unit === "m3" ? "m³" : unit),
      aankomststand: "Aankomststand",
      vertrekstand: "Vertrekstand",
      verbruik: "Verbruik",
      schade: "Schade",
      schadeHint: "In hele euro's. Laat op 0 zonder schade.",
      aanwezigen: "Aanwezigen",
      // WHY the screen asks: the subsidieaangifte (§7) rekent op de echte
      // aanwezigheid, en die valt jaren later niet meer na te vragen.
      aanwezigenUitleg:
        "Tel wie er echt was: de subsidieaangifte rekent op deze aantallen, en die kan je de groep later niet meer vragen.",
      aantalPersonen: "Personen",
      aantalOnder31: "Van wie onder 31",
      // Leeftijd telt, niet het soort groep (werkingssubsidie, §7).
      onder31Hint: "Leeftijd op de aankomstdag telt, niet het soort groep.",
      aanwezigenOnvolledig: "Vul in hoeveel personen er waren en hoeveel er onder 31 waren.",
      onder31TeVeel: "Er kunnen niet meer personen onder 31 zijn dan er aanwezig waren.",
      waarborgOntvangen: "Waarborg ontvangen",
      geenWaarborg: "Geen waarborg ontvangen voor dit verblijf.",
      terugTeStorten: "Terug te storten",
      nogTeOntvangen: "Nog te ontvangen",
      inEvenwicht: "Alles verrekend, niets terug te storten.",
      bewaar: "Bewaar meterstanden",
      p1Knop: "Lees automatisch uit (HomeWizard P1)",
      p1Binnenkort: "Binnenkort: de slimme meter leest zichzelf uit.",
      bewaard: "Meterstanden bewaard.",
      rekenAf: "Reken af",
      rekenAfUitleg: "Bewaart de meterstanden en sluit de afrekening. Daarna ligt alles vast.",
      onvolledig: "Vul voor elke meter een aankomst- en vertrekstand in.",
      negatief: "Vertrekstand ligt onder de aankomststand.",
      geenPrijs: "Geen eenheidsprijs bekend.",
      prijsOpVerblijf: (datum: string) => `Eenheidsprijzen zoals geldig op ${datum}.`,
      afgerekendOp: (moment: string) => `Afgerekend op ${moment}.`,
      heropen: "Heropen afrekening",
      heropenZeker:
        "Heropen afrekening — zeker? De vastgelegde afrekening vervalt en de meterstanden worden weer aanpasbaar.",
      bekijk: "Bekijk eindafrekening",
      terugNaarBoeking: "Boeking",
    },
    conflict: {
      boeking: (naam: string, status: string) => `Overlapt met ${naam} (${status.toLowerCase()}).`,
      blokkade: (reden: string) => `Overlapt met een gesloten periode: ${reden}.`,
    },
    berichten: {
      knop: "Kopieer bericht voor de groep",
      gekopieerd: "Gekopieerd — plak het in je e-mail.",
      kopieMislukt: "Kopiëren lukte niet — selecteer en kopieer de tekst hieronder zelf.",
      // Ready-to-paste Dutch: after every click the admin owes the group a
      // mail, and writing it was homework the app silently left behind
      // (persona review). "je" — the request-flow register.
      option: (b: { naam: string; periode: string; totaal: string; vervaldag: string }) =>
        `Dag ${b.naam}\n\nGoed nieuws: jullie aanvraag voor ${b.periode} staat in optie. ` +
        `De prijs bedraagt ${b.totaal}.\n\nDe optie vervalt automatisch op ${b.vervaldag} — ` +
        `laat voor die datum weten of jullie bevestigen.\n\nGroeten\nChiro Sint-Jozef Rijkevorsel`,
      confirmed: (b: { naam: string; periode: string; totaal: string; waarborg: string }) =>
        `Dag ${b.naam}\n\nJullie verblijf van ${b.periode} is bevestigd! ` +
        `De huurprijs bedraagt ${b.totaal}.\n\nDe waarborg bedraagt ${b.waarborg} — als je die ` +
        `nog niet stortte, graag voor jullie aankomst.\n\nTot dan!\nChiro Sint-Jozef Rijkevorsel`,
      settled: (b: { naam: string; periode: string; ingehouden: string; terug: string }) =>
        `Dag ${b.naam}\n\nBedankt voor jullie verblijf van ${b.periode}. De eindafrekening is ` +
        `klaar: van de waarborg houden we ${b.ingehouden} in voor verbruik en eventuele schade, ` +
        `en we storten ${b.terug} terug.\n\nGroeten\nChiro Sint-Jozef Rijkevorsel`,
      // The forfeited-and-then-some case: the mail must NOT close the books
      // while money is still owed (adversarial-review find).
      settledTekort: (b: { naam: string; periode: string; ingehouden: string; tekort: string }) =>
        `Dag ${b.naam}\n\nBedankt voor jullie verblijf van ${b.periode}. De eindafrekening is ` +
        `klaar: het verbruik en eventuele schade overstijgen de waarborg. We houden de volledige ` +
        `waarborg van ${b.ingehouden} in, en er blijft ${b.tekort} te betalen. Jullie ontvangen ` +
        `hiervoor nog een afrekening.\n\nGroeten\nChiro Sint-Jozef Rijkevorsel`,
    },
    acties: {
      // No "kan niet ongedaan gemaakt worden": since 0.2b almost everything
      // CAN be undone — the prompt guards against a mis-tap, not doom.
      zeker: (actie: string) => `${actie} — ben je zeker?`,
      hold_option: "Neem optie",
      decline: "Weiger",
      confirm: "Bevestig boeking",
      cancel: "Annuleer",
      record_stay: "Registreer verblijf",
      settle: "Reken af",
      close: "Sluit af",
      expire: "Laat vervallen",
      un_decline: "Maak weigering ongedaan",
      un_cancel: "Maak annulering ongedaan",
      revive: "Heropen aanvraag",
      reopen: "Heropen afrekening",
      un_record: "Maak registratie ongedaan",
    },
  },

  placeholder: {
    kop: "Deze pagina volgt",
    uitleg: (ticket: string) => `Dit onderdeel volgt met ticket ${ticket}.`,
  },

  status: {
    enquiry: "Aanvraag",
    option: "In optie",
    confirmed: "Bevestigd",
    stayed: "Verblijf afgerond",
    settled: "Afgerekend",
    closed: "Afgesloten",
    declined: "Geweigerd",
    expired: "Vervallen",
    cancelled: "Geannuleerd",
  },

  fouten: {
    datumsBezet: "Deze datums zijn intussen bezet.",
    nietGevonden: "Niet gevonden.",
    verplichtVeld: "Dit veld is verplicht.",
    kalenderNietGeladen: "De kalender kon niet geladen worden.",
  },

  footer: {
    beheer: "Beheer",
    contactKop: "Contact",
    email: "verhuur@example.com",
    adres: "Zuiderdijk 10E, 2310 Rijkevorsel",
    kleineLetters: "Een initiatief van Chiro Sint-Jozef Rijkevorsel vzw.",
  },
} as const;
